import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import capitalize from 'lodash/capitalize'
import { OrderingProvider } from 'models/Merchant'
import React from 'react'
import { Link } from 'react-router-dom'

const t = buildTranslate('pos_configurations')

const EmptyShell = ({
  icon,
  title,
  description,
  cta,
}: {
  icon: string
  title: string
  description: string
  cta?: React.ReactNode
}) => {
  const [css] = useStyletron()
  return (
    <div
      className={`d-flex flex-column align-items-center text-center p-xl ${css({
        backgroundColor: palette.white,
        border: `1px solid ${palette.grey20}`,
        borderRadius: '4px',
      })}`}
    >
      <i
        className={`fa ${icon} mb-m ${css({
          color: palette.grey50,
          fontSize: '48px',
        })}`}
        aria-hidden="true"
      />
      <h3
        className={`bold mb-xs ${css({
          color: palette.grey90,
          fontSize: '20px',
          lineHeight: '28px',
        })}`}
      >
        {title}
      </h3>
      <p
        className={`body-text-3 mb-0 ${css({
          color: palette.grey70,
          maxWidth: '560px',
        })}`}
      >
        {description}
      </p>
      {cta && <div className="mt-m">{cta}</div>}
    </div>
  )
}

// Pre-launch placeholder rendered when the menu-errors feature flag is
// off. Existed as an inline render in PosConfigurations/index.tsx; lifted
// here so the route shell can dispatch to one of the four empty states
// based on the response payload.
export const PreLaunchEmptyState = () => (
  <EmptyShell
    icon="fa-clipboard-list"
    title={t('empty_title') as string}
    description={t('empty_description') as string}
  />
)

// Decision 1 (2026-05-15) — Olo menus are not shared across locations, so
// menu-grouped errors don't apply. BE returns `skipped_provider: "olo"`
// and the FE redirects the operator to the per-location view.
export const OloEmptyState = () => (
  <EmptyShell
    icon="fa-info-circle"
    title={t('empty_olo_title') as string}
    description={t('empty_olo_description') as string}
    cta={
      <Link className="btn btn-primary" to="/locations">
        {t('empty_olo_cta') as string}
      </Link>
    }
  />
)

export const ProviderSkipEmptyState = ({
  provider,
}: {
  provider: OrderingProvider
}) => (
  <EmptyShell
    icon="fa-info-circle"
    title={
      t('empty_provider_skip_title', {
        provider: capitalize(provider),
      }) as string
    }
    description={t('empty_provider_skip_description') as string}
  />
)

// Happy path — merchant has menus but none currently surface errors.
export const HealthyEmptyState = () => (
  <EmptyShell
    icon="fa-check-circle"
    title={t('empty_healthy_title') as string}
    description={t('empty_healthy_description') as string}
  />
)

// Rendered when the list query errors out, so a failed request shows an
// actionable message instead of a blank content area.
export const ErrorEmptyState = () => (
  <EmptyShell
    icon="fa-exclamation-triangle"
    title={t('empty_error_title') as string}
    description={t('empty_error_description') as string}
  />
)
